import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/common/common-components/ui/Card";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/app/common/common-components/ui/RadioGroup";
import { Switch } from "@/app/common/common-components/ui/Switch";
import { formatNumber, formatInputNumber } from "@/app/common/common-functions/functions";

interface CommissionCalculatorProps {
  countryCode?: string;
  currencySymbol?: string;
}

export const CommissionCalculator = ({ countryCode = "RSA", currencySymbol = "R" }: CommissionCalculatorProps) => {
  const [mode, setMode] = useState<"forward" | "backward">("forward");
  const [salePrice, setSalePrice] = useState("");
  const [ownerNet, setOwnerNet] = useState("");
  const [commissionRate, setCommissionRate] = useState<number | null>(5);
  const [vatOnCommission, setVatOnCommission] = useState(true);
  const vatRate = 15; // 15% VAT

  const handleNumberInput = (value: string, setter: (value: string) => void) => {
    // Only allow numbers and spaces
    const numericValue = value.replace(/[^0-9 ]/g, "");
    setter(numericValue);
  };

  // Calculation logic
  let commission = 0,
    vat = 0,
    net = 0,
    requiredSalePrice = 0;
  const salePriceNum = parseFloat(salePrice.replace(/ /g, "")) || 0;
  const ownerNetNum = parseFloat(ownerNet.replace(/ /g, "")) || 0;

  if (mode === "forward" && salePriceNum > 0) {
    commission = (salePriceNum * (commissionRate || 0)) / 100;
    vat = vatOnCommission ? commission * (vatRate / 100) : 0;
    net = salePriceNum - commission - vat;
  } else if (mode === "backward" && ownerNetNum > 0) {
    // Reverse calculation: ownerNet = salePrice - commission - vat
    // commission = salePrice * (commissionRate/100)
    // vat = commission * (vatRate/100) if applicable
    // So: ownerNet = salePrice - salePrice*rate - salePrice*rate*vatRate
    // Let x = salePrice
    // ownerNet = x - x*r - x*r*v = x*(1 - r - r*v)
    // x = ownerNet / (1 - r - r*v)
    const r = (commissionRate || 0) / 100;
    const v = vatOnCommission ? vatRate / 100 : 0;
    requiredSalePrice = ownerNetNum / (1 - r - r * v);
    commission = requiredSalePrice * r;
    vat = commission * v;
    net = requiredSalePrice - commission - vat;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Commission Calculator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="tw-flex tw-flex-col tw-gap-8 md:tw-flex-row">
          <div className="tw-w-full md:tw-w-1/2">
            <div className="tw-space-y-4">
              <div className="tw-space-y-2">
                <Label>Calculation Mode</Label>
                <RadioGroup
                  value={mode}
                  onValueChange={(val) => {
                    const newMode = val as "forward" | "backward";
                    if (newMode === "forward" && ownerNet) {
                      // When switching to forward mode, set sale price to owner's net
                      setSalePrice(ownerNet);
                    } else if (newMode === "backward" && salePrice) {
                      // When switching to backward mode, set owner's net to sale price
                      setOwnerNet(salePrice);
                    }
                    setMode(newMode);
                  }}
                  className="tw-flex tw-space-x-4"
                >
                  <div className="tw-flex tw-items-center tw-space-x-2">
                    <RadioGroupItem value="forward" id="forward" />
                    <Label htmlFor="forward">From Sale Price</Label>
                  </div>
                  <div className="tw-flex tw-items-center tw-space-x-2">
                    <RadioGroupItem value="backward" id="backward" />
                    <Label htmlFor="backward">From Owner's Net</Label>
                  </div>
                </RadioGroup>
              </div>
              {mode === "forward" ? (
                <div className="tw-space-y-2">
                  <Label htmlFor="salePrice">Sale Price ({currencySymbol})</Label>
                  <Input
                    id="salePrice"
                    type="text"
                    value={formatInputNumber(salePrice)}
                    onChange={(e) => handleNumberInput(e.target.value, setSalePrice)}
                    className="tw-w-full"
                  />
                </div>
              ) : (
                <div className="tw-space-y-2">
                  <Label htmlFor="ownerNet">Owner's Net Amount ({currencySymbol})</Label>
                  <Input
                    id="ownerNet"
                    type="text"
                    value={formatInputNumber(ownerNet)}
                    onChange={(e) => handleNumberInput(e.target.value, setOwnerNet)}
                    className="tw-w-full"
                  />
                </div>
              )}
              <div className="tw-space-y-2">
                <Label htmlFor="commissionRate">Commission Rate (%)</Label>
                <Input
                  id="commissionRate"
                  type="number"
                  value={commissionRate || ""}
                  onChange={(e) => setCommissionRate(parseFloat(e.target.value))}
                  className="tw-w-full"
                />
              </div>
              <div className="tw-flex tw-items-center tw-space-x-2">
                <Switch id="vatOnCommission" checked={vatOnCommission} onCheckedChange={setVatOnCommission} />
                <Label htmlFor="vatOnCommission">Include VAT on commission ({vatRate}%)</Label>
              </div>
            </div>
          </div>
          <div className="tw-mt-8 tw-w-full md:tw-mt-0 md:tw-w-1/2">
            <div className="tw-space-y-4 tw-rounded-lg tw-bg-muted tw-p-4">
              <div className="tw-text-lg tw-font-semibold">Results</div>
              {mode === "forward" ? (
                <>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">Sale Price:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(salePriceNum)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">Commission:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(commission)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">VAT on Commission:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(vat)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">Owner's Net:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(net)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">Required Sale Price:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(requiredSalePrice)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">Commission:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(commission)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">VAT on Commission:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(vat)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-font-medium">Owner's Net:</span>
                    <span className="tw-font-semibold">
                      {currencySymbol} {formatNumber(net)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
